import * as R from 'ramda'
import { removeAbbr } from '@/utils/time'
import { fillDate } from '@/components/patient-stats/common/utils'
import {
  buildAxis,
  buildMarkLine,
  getMarkLineRange,
} from '@/components/patient-stats/graphs/detailed-reports/options/option-builders'
import moment from 'moment-timezone'
import { CHART_DATE_FORMAT } from '@/components/patient-stats/constants'
import { DataPoint, InhalerTypes, InhalerUsage } from '@/jsonapi/CommonTypes'
import { withSingularOrPluralLabel } from '@/utils/labels'
import YAxis = echarts.EChartOption.YAxis
import XAxis = echarts.EChartOption.XAxis

type InhalerUsagePuffsDataPoint = DataPoint & {
  name: string
  startOfDay: number
  index: number
  type: InhalerTypes
}

export function buildInhalerPuffsOptions(options: {
  resultStartTimestamp: number
  resultEndTimestamp: number
  timezone: string
  colors: Record<any, any> // vuetify theme
  inhalerUsages: InhalerUsage[]
}): echarts.EChartOption<echarts.EChartOption.SeriesBar> {
  const { timezone, resultStartTimestamp, resultEndTimestamp, colors, inhalerUsages } = options

  const numOfDataByDay = {}

  const dataPoints: InhalerUsagePuffsDataPoint[] = inhalerUsages
    .map(inhalerUsage =>
      inhalerUsage.dataPoints
        .filter(dataPoint => dataPoint.value > 0)
        .map(dataPoint => {
          const startOfDay = moment(dataPoint.timestamp)
            .tz(timezone)
            .startOf('day')
            .valueOf()
          if (numOfDataByDay[startOfDay] == null) {
            numOfDataByDay[startOfDay] = 0
          } else {
            numOfDataByDay[startOfDay] += 1
          }

          return {
            ...dataPoint,
            name: inhalerUsage.name,
            startOfDay,
            index: numOfDataByDay[startOfDay],
            type: inhalerUsage.type,
          }
        })
    )
    .flat(1)

  const dataPointsByIndex = R.groupBy<InhalerUsagePuffsDataPoint>(dataPoint => String(dataPoint.index), dataPoints)

  const xAxisLabels: string[] = [...fillDate(timezone, resultStartTimestamp, resultEndTimestamp)].map(timestamp =>
    String(timestamp)
  )

  const maxYValue = Math.max(5, ...dataPoints.map(dp => dp.value))
  const lines = getMarkLineRange({
    minStart: 1,
    maxYValue,
    numOfSeparate: 5,
    minEnd: 5,
    minStep: 1,
    ceilSeparateNum: true,
  })

  return {
    grid: [{ x: '2.5%', y: '5%', width: '95%', height: '95%', containLabel: true } as echarts.EChartOption.Grid],
    xAxis: buildAxis({
      data: xAxisLabels,
      axisType: 'x',
      formatter: param => {
        return moment(parseInt(param, 10)).format(removeAbbr(timezone, CHART_DATE_FORMAT.DATE_ABBR))
      },
    }) as XAxis,
    yAxis: buildAxis({
      axisType: 'y',
      maxValue: maxYValue,
    }) as YAxis,
    // @ts-ignore
    series: [
      {
        type: 'bar',
        silent: true,
        // @ts-ignore
        markLine: buildMarkLine(lines),
      },
      // @ts-ignore
      ...Object.entries(dataPointsByIndex).map(([index, _dataPoints]) => {
        return {
          type: 'bar',
          name: `data-${index}`,
          barMinHeight: 3,
          barGap: '10%',
          itemStyle: {
            barBorderRadius: [2, 2, 0, 0],
          },
          data: _dataPoints.map(dataPoint => ({
            value: [String(dataPoint.startOfDay), dataPoint.value, dataPoint],
            itemStyle: {
              color: dataPoint.value >= 3 ? colors.burgundy : colors.teal,
            },
          })),
        }
      }),
    ],
    tooltip: {
      trigger: 'item',
      formatter: params => {
        if (!('data' in params)) {
          return null
        }

        const data: InhalerUsagePuffsDataPoint = params.data.value[params.data.value.length - 1]
        const dateLabel = moment(data.timestamp)
          .tz(timezone)
          .format(removeAbbr(timezone, CHART_DATE_FORMAT.TIME_ABBR))
        const puffsLabel = withSingularOrPluralLabel(data.value, 'puff', 'puffs')
        return `${dateLabel}<br>${data.name} ${puffsLabel}`
      },
    },
  }
}
